// src/features/analytics/components/AnalyticsRangeControl.tsx

import { useEffect, useState } from 'react';
import { Group, Select, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import { useNavigate } from '@tanstack/react-router';
import type { DayZone } from '@/shared/utils/utcRange';
import { analyticsRouteApi } from '../routeApi';
import { useAnalyticsRange } from '../useAnalyticsRange';
import {
  ANALYTICS_PRESET_LABELS,
  ANALYTICS_RANGE_PRESETS,
  type AnalyticsRangePreset,
  type AnalyticsSearch,
} from '../searchParams';

function toDateString(seconds: number, zone: DayZone): string {
  return DateTime.fromSeconds(seconds, { zone }).toFormat('yyyy-MM-dd');
}

function customValueFor(search: AnalyticsSearch, zone: DayZone): [string | null, string | null] {
  return search.preset === 'custom' && search.from !== undefined && search.to !== undefined
    ? [toDateString(search.from, zone), toDateString(search.to - 1, zone)]
    : [null, null];
}

/**
 * Preset selector + custom calendar-day range picker. Writes URL search
 * params; ranges are calendar days, end-exclusive (`to` = midnight after the
 * last picked day) — UTC days by default, the browser's LOCAL days once the
 * gateway declares `features.tz` (dashboard ADR-009). The picker floor
 * follows the product window when configured.
 */
export function AnalyticsRangeControl() {
  const search = analyticsRouteApi.useSearch();
  const navigate = useNavigate();
  const { range, productWindowFloorSeconds, zone, localDays } = useAnalyticsRange();

  const setSearch = (patch: Partial<AnalyticsSearch>) => {
    void navigate({
      to: '.',
      search: (prev) => ({ ...(prev as AnalyticsSearch), ...patch }),
    });
  };

  const handlePresetChange = (value: string | null) => {
    if (!value) return;
    const preset = value as AnalyticsRangePreset;
    if (preset === 'custom') {
      // Seed the custom range from the currently resolved range so the URL
      // stays valid (custom without from/to degrades to the default preset).
      setSearch({ preset, from: range.from, to: range.to });
    } else {
      setSearch({ preset, from: undefined, to: undefined });
    }
  };

  // Hold the in-progress selection locally so the first click survives. A fully
  // controlled range picker fed a partial `[start, null]` back as `[null, null]`
  // resets Mantine's pick and the range can never complete; commit to the URL
  // only when both ends are set, and resync when the resolved range changes.
  const [pending, setPending] = useState<[string | null, string | null]>(() =>
    customValueFor(search, zone)
  );

  useEffect(() => {
    setPending(customValueFor(search, zone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.preset, search.from, search.to, zone]);

  const handleCustomChange = (value: [string | null, string | null]) => {
    setPending(value);
    const [start, end] = value;
    if (!start || !end) return;
    const from = DateTime.fromFormat(start, 'yyyy-MM-dd', { zone });
    const lastDay = DateTime.fromFormat(end, 'yyyy-MM-dd', { zone });
    if (!from.isValid || !lastDay.isValid) return;
    setSearch({
      preset: 'custom',
      from: from.toSeconds(),
      to: lastDay.plus({ days: 1 }).toSeconds(),
    });
  };

  const today = toDateString(Math.floor(Date.now() / 1000), zone);
  const zoneAbbrev = localDays ? DateTime.local().toFormat('ZZZZ') : 'UTC';

  return (
    <Group gap="sm" align="center">
      <Select
        data={ANALYTICS_RANGE_PRESETS.map((preset) => ({
          value: preset,
          label: ANALYTICS_PRESET_LABELS[preset],
        }))}
        value={search.preset}
        onChange={handlePresetChange}
        allowDeselect={false}
        w={150}
        size="sm"
        aria-label="Date range preset"
      />
      {search.preset === 'custom' && (
        <DatePickerInput
          type="range"
          allowSingleDateInRange
          value={pending}
          onChange={handleCustomChange}
          minDate={
            productWindowFloorSeconds !== null
              ? toDateString(productWindowFloorSeconds, zone)
              : undefined
          }
          maxDate={today}
          size="sm"
          w={260}
          aria-label={localDays ? 'Custom date range (local)' : 'Custom date range (UTC)'}
        />
      )}
      <Text size="xs" c="dimmed">
        {localDays ? `Dates are local (${zoneAbbrev})` : 'Dates are UTC'}
      </Text>
    </Group>
  );
}
